import React, { useState, useEffect, useCallback, useRef } from "react";
import Navbar from "../../components/Navbar/Navbar";
import { Breadcrumb, ConfigProvider } from "antd";
import { DoubleRightOutlined, DownloadOutlined } from "@ant-design/icons";
import ShimmerLoader from "../../loading";
import "../../styles.css";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { routes } from "../../Routes";
import LoadingSpinner from "../../components/Loading";
import {
  getPurchaseList,
  getVendorDetails,
  getVendorInventoryDetails,
  getVendorProducts,
} from "../../API  Functions/PurchaseFunctions";
import VendorTiles from "./VendorInfoTiles";
import dayjs from "dayjs";
import SearchBox from "../../components/CustomWidgets/SearchBox";
import moment from "moment";
import TopBottomProductsTable from "./PurchaseTables/TopBottomProductsTable";
import AllProductsTable from "./PurchaseTables/AllProductsTable";
import axiosInstance from "../../AxiosInstance";
import i18next from "i18next";
import { URLS } from "../../API/API endpoints";
import { toast } from "react-toastify";
import PurchasePayment from "../../modals/purchasePaymentModal";
import VendorHighLowTiles from "./VendorHighLowTiles";
import { checkPermission } from "../../Utils/permissionHelper";
import DateFilter from "../../components/ui/DateFilter";
import VendorPaymentTable from "./VendorPaymentTable";

const getStartOfMonth = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}-01`;
};

const getCurrentDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

// Helper: map paymentType number → state param string (mirror your existing logic)
const toStateParam = (paymentType) => {
  switch (paymentType) {
    case 1:  return "purchase";
    case 2:  return "done";
    case 3:  return "cancel";
    default: return "";
  }
};

// Helper: flatten a purchase row (mirror your existing flattenRow logic)
const flattenRow = (val) => {
  const od = val?.order_details ?? {};
  return {
    // ── original vendor-payment fields ──────────────────────────────────────
    order_no:         od.order_no       ?? "",
    supplier_name:    od.partner_name   ?? "",
    supplier_id:      od.partner_id     ?? null,
    order_date:       od.order_date     ?? "",
    vendor_reference: od.venfor_ref     ?? "",
    invoice_value:    od.total_amount   ?? 0,
    paid_amount:      od.paid_amount    ?? 0,
    due_amount:       od.due_amount     ?? 0,
    store_name:       od.store_name     ?? "",
    bill_id:          od.bill_id        ?? null,
    // ── new fields from purchase history ────────────────────────────────────
    status:           od.status         ?? "",
    payment_status:   od.payment_status ?? "",
    create_date:      od.create_date    ?? "",
    due_date:         od.due_date       ?? "",
    payment_term:     od.payment_term   ?? "",
    quantity:         val.lines?.reduce((s, l) => s + (l.product_qty ?? 0), 0) ?? 0,
    // ── raw original kept so row-click can pass full orderData ───────────────
    _raw: val,
  };
};

const VendorInfo = () => {
  const { t, i18n } = useTranslation();
  const company_id = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );
  const user_id = useSelector(
    (state) => state?.login?.user?.result?.user_id,
  );

  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [searchByShortCode, setSearchByShortCode] = useState(false);
  const [searchid, setSearchId] = useState(localStorage.getItem("searchid"));
  const [vendorData, setVendorData] = useState({});
  const [vendorInventoryData, setVendorInventoryData] = useState({});

  // Products section state
  const [productActiveTab, setProductActiveTab] = useState("orders");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRange, setSelectedRange] = useState("thisMonth");
  const [startDate, setStartDate] = useState(getStartOfMonth());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [productData, setProductData] = useState([]);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [currentOffset, setCurrentOffset] = useState(0);

  // Orders section state
  const [orderActiveTab, setOrderActiveTab] = useState("orders");
  const [orderData, setOrderData] = useState([]);
  const [orderTableLoading, setOrderTableLoading] = useState(true);
  const [orderCurrentLimit, setOrderCurrentLimit] = useState(10);
  const [orderCurrentOffset, setOrderCurrentOffset] = useState(0);
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [orderIsSearching, setOrderIsSearching] = useState(false);

  const [vendorID, setVendorId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [popup, setpopup] = useState(false);

  // Refs so debounced/async callbacks always read latest values
  const searchidRef = useRef(searchid);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  const productActiveTabRef = useRef(productActiveTab);
  const searchQueryRef = useRef(searchQuery);
  const searchByShortCodeRef = useRef(searchByShortCode);
  const orderActiveTabRef = useRef(orderActiveTab);
  const orderSearchQueryRef = useRef(orderSearchQuery);
  const orderCurrentLimitRef = useRef(orderCurrentLimit);

  const isGinne = useSelector((state) => state?.geneLoginReducer?.isGinneLogin);
  const location = useLocation();
  const fromPage = location.state?.from;

  // Keep all refs in sync with state
  useEffect(() => { searchidRef.current = searchid; }, [searchid]);
  useEffect(() => { startDateRef.current = startDate; }, [startDate]);
  useEffect(() => { endDateRef.current = endDate; }, [endDate]);
  useEffect(() => { productActiveTabRef.current = productActiveTab; }, [productActiveTab]);
  useEffect(() => { searchQueryRef.current = searchQuery; }, [searchQuery]);
  useEffect(() => { searchByShortCodeRef.current = searchByShortCode; }, [searchByShortCode]);
  useEffect(() => { orderSearchQueryRef.current = orderSearchQuery; }, [orderSearchQuery]);
  useEffect(() => { orderCurrentLimitRef.current = orderCurrentLimit; }, [orderCurrentLimit]);

  // ─── Order tab → paymentType mapping ─────────────────────────────────────
  // "all" = 0, "top" = reuse top-5 filter, "bottom" = bottom-5 filter
  // Adjust paymentType values to match your existing logic if needed
  const orderTabToPaymentType = (tab) => {
    switch (tab) {
      case "top":    return 1; // e.g. "purchase" / confirmed
      case "bottom": return 2; // e.g. "done" / received
      default:       return 0; // all
    }
  };

  // Initial Load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (id) localStorage?.setItem("vendorId", id);
    const searchParam = id || localStorage.getItem("vendorId");
    setSearchId(searchParam);

    if (searchParam) {
      setLoading(true);
      getVendorInfo(searchParam, getStartOfMonth(), getCurrentDate());
      getVendorInventoryInfo(searchParam);
      fetchVendorProducts("", getStartOfMonth(), getCurrentDate(), "all", searchParam, 10, 0);
      fetchOrderData("", 0, 0, getStartOfMonth(), getCurrentDate(), searchParam, 10);
    }
  }, []);

  // ─── Product API Calls ────────────────────────────────────────────────────
  const getVendorInfo = async (vendorId, start_date = "", end_date = "") => {
    setLoading(true);
    const data = await getVendorDetails(company_id, vendorId, start_date, end_date);
    setVendorData(data || {});
    setLoading(false);
  };

  const refreshVendorData = () => {
    getVendorInfo(searchid, startDate, endDate);
  };

  const getVendorInventoryInfo = async (vendorId) => {
    setLoading(true);
    const response = await getVendorInventoryDetails(company_id, vendorId);
    if (response?.status_code === 200 && response?.data) {
      setVendorInventoryData(response.data);
    } else {
      setVendorInventoryData({
        low_on_inventory: 0,
        high_on_inventory: 0,
        out_of_stock: 0,
        healthy_inventory: 0,
      });
    }
    setLoading(false);
  };

  const fetchVendorProducts = async (
    search = "",
    start = "",
    end = "",
    filter = "all",
    searchParam,
    limit = 10,
    offset = 0,
  ) => {
    try {
      const isByShortCode = searchByShortCodeRef.current;
      const searchkey = isByShortCode ? "" : search;
      const shortkey = isByShortCode ? search : "";

      const result = await getVendorProducts(
        company_id,
        searchParam,
        searchkey,
        start,
        end,
        filter,
        limit,
        offset,
        shortkey,
      );
      setProductData(result || []);
    } catch (error) {
      console.error("Error fetching vendor products:", error);
    } finally {
      setTableLoading(false);
    }
  };

  // ─── Orders API Call ──────────────────────────────────────────────────────
  const fetchOrderData = (
    search      = "",
    paymentType = 0,
    offset      = 0,
    start       = startDateRef.current,
    end         = endDateRef.current,
    vendorIdParam = searchidRef.current,
    limit       = orderCurrentLimitRef.current,
  ) => {
    if (search) setOrderIsSearching(true);
    else        setOrderTableLoading(true);

    const payload = {
      lang:       i18next.language === "ar" ? "ar_001" : "en_US",
      user_id,
      state:      toStateParam(paymentType),
      order_no:   search,
      limit,
      offset,
      start_date: start || "",
      end_date:   end   || "",
      ...(vendorIdParam && { vendor_id: Number(vendorIdParam) }),
    };

    getPurchaseList(payload)
      .then((res) => {
        if (res?.result?.[0]?.status_code === 200) {
          setOrderData(res.result[0].response.map(flattenRow));
        } else {
          setOrderData([]);
        }
        setOrderIsSearching(false);
        setOrderTableLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching purchase list:", err);
        setOrderIsSearching(false);
        setOrderTableLoading(false);
      });
  };

  // ─── Date Filter ──────────────────────────────────────────────────────────
  const handleDateFilter = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentLimit(10);
    setCurrentOffset(0);
    setOrderCurrentLimit(10);
    setOrderCurrentOffset(0);
    getVendorInfo(searchid, start, end);
    fetchVendorProducts(searchQueryRef.current, start, end, productActiveTabRef.current, searchid, 10, 0);
    fetchOrderData("", orderTabToPaymentType(orderActiveTabRef.current), 0, start, end, searchidRef.current, 10);
  };

  // ─── Product search ───────────────────────────────────────────────────────
  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const handleSearchChange = useCallback(
    debounce((value) => {
      setSearchQuery(value);
      setCurrentLimit(10);
      setCurrentOffset(0);
      fetchVendorProducts(
        value,
        startDateRef.current,
        endDateRef.current,
        productActiveTabRef.current,
        searchidRef.current,
        10,
        0,
      );
    }, 1000),
    [],
  );

  const handleShortCodeToggle = () => {
    const turningOff = searchByShortCode;
    setSearchByShortCode(!searchByShortCode);
    if (turningOff) {
      setSearchQuery("");
      setCurrentLimit(10);
      setCurrentOffset(0);
      setTimeout(() => {
        fetchVendorProducts(
          "",
          startDateRef.current,
          endDateRef.current,
          productActiveTabRef.current,
          searchidRef.current,
          10,
          0,
        );
      }, 0);
    }
  };

  // ─── Order search ─────────────────────────────────────────────────────────
  const handleOrderSearchChange = useCallback(
    debounce((value) => {
      setOrderSearchQuery(value);
      setOrderCurrentLimit(10);
      setOrderCurrentOffset(0);
      fetchOrderData(
        value,
        orderTabToPaymentType(orderActiveTabRef.current),
        0,
        startDateRef.current,
        endDateRef.current,
        searchidRef.current,
        10,
      );
    }, 1000),
    [],
  );

  // ─── Product tab switch ───────────────────────────────────────────────────
  const handleProductTabChange = (tab) => {
    setProductActiveTab(tab);
    setSearchQuery("");
    setCurrentLimit(10);
    setCurrentOffset(0);
    fetchVendorProducts("", startDate, endDate, tab, searchid, 10, 0);
  };

  // ─── Export ───────────────────────────────────────────────────────────────
  const handleExport = async () => {
    const isByShortCode = searchByShortCodeRef.current;
    const searchkey = isByShortCode ? "" : searchQuery;
    const shortkey = isByShortCode ? searchQuery : "";

    const payload = {
      lang: i18next.language === "ar" ? "ar_001" : "en_US",
      company_id,
      vendor_id: Number(searchid),
      search: searchkey,
      short_code: shortkey,
      filter: productActiveTab,
      start_date: startDate,
      end_date: endDate,
    };

    try {
      const response = await axiosInstance.post(URLS.DOWNLOAD_VENDOR_PRODUCTS_EXCEL, payload);
      if (response?.data?.result?.status_code === 200) {
        const fileData = response?.data?.result?.file || response?.data?.result?.file_base64;
        downloadFile(fileData);
      } else {
        toast.error(response?.data?.result?.status || t("Export failed"));
      }
    } catch (error) {
      console.error("Vendor products export failure:", error);
      toast.error(t("Something went wrong while exporting"));
    }
  };

  const downloadFile = (base64Data) => {
    try {
      if (!base64Data || typeof base64Data !== "string" || base64Data.trim() === "") {
        alert("No valid file data available for download");
        return;
      }
      let cleanBase64 = base64Data
        .trim()
        .replace(/^data:[\w/+-]+;base64,/, "")
        .replace(/^["']|["']$/g, "")
        .replace(/\s/g, "");
      const padLength = 4 - (cleanBase64.length % 4);
      if (padLength < 4) cleanBase64 += "=".repeat(padLength);

      const binaryString = atob(cleanBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);

      const header = new Uint8Array(bytes.buffer, 0, 8);
      let mimeType = "application/octet-stream";
      let fileExtension = "bin";

      if (header[0] === 0x50 && header[1] === 0x4b) {
        mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        fileExtension = "xlsx";
      } else if (header[0] === 0xd0 && header[1] === 0xcf) {
        mimeType = "application/vnd.ms-excel";
        fileExtension = "xls";
      } else if (new TextDecoder().decode(header).includes(",")) {
        mimeType = "text/csv";
        fileExtension = "csv";
      }

      const blob = new Blob([bytes], { type: mimeType });
      const timestamp = moment().format("YYYY-MM-DD_HH.mm.ss");
      const filename = `${t("vendor_products")}_${timestamp}.${fileExtension}`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("File download failed:", error);
      alert(`Download failed: ${error.message || "Unknown error"}`);
    }
  };

  const handlePaymentClick = () => {
    setVendorId(searchid);
    setIsModalVisible(true);
  };

  return (
    <>
      {loading && <LoadingSpinner />}
      <div className="w-full flex flex-col md:h-full p-2 overflow-y-auto bg-[#F6F7F9]">
        <Navbar heading="Vendor Details" />
        <div className="flex-grow overflow-y-auto custom-scrollbar overflow-hidden ms-3">
          <>
            <div className="font-extrabold">
              <Breadcrumb
                separator={<DoubleRightOutlined />}
                items={[
                  {
                    title: (
                      <Link
                        to={
                          fromPage === "vendor_payment"
                            ? routes.purchase.supplyPayment
                            : routes.purchase.vendors
                        }
                      >
                        {fromPage === "vendor_payment"
                          ? t("Vendor Invoices")
                          : t("Vendor List")}
                      </Link>
                    ),
                    key: fromPage === "vendor_payment" ? "vendor_payment" : "all_vendors",
                  },
                  { title: t("Vendor Details") },
                ]}
                className="relative mt-3 font-semibold"
              />
            </div>

            {/* Date Filter */}
            <div className="flex justify-end mt-4 me-4">
              <ConfigProvider direction={i18n.language === "ar" ? "rtl" : "ltr"}>
                <DateFilter
                  setSelectedRange={setSelectedRange}
                  selectedRange={selectedRange}
                  SubmitFilter={(start, end) => handleDateFilter(start, end)}
                  isAllTime={true}
                />
              </ConfigProvider>
            </div>

            {/* Vendor Header */}
            <div className="bg-white mt-3 rounded-lg">
              <div className="flex flex-col md:flex-row items-start md:items-end justify-between bg-white rounded-lg me-4 p-4">
                <div className="flex flex-col md:gap-2 md:ms-4 text-gray-800">
                  <h3 className="text-sm font-medium">{t("Vendor Name")}</h3>
                  <p>
                    <span className="text-xl font-semibold">
                      {vendorData?.vendor_name || "--"}
                    </span>
                  </p>
                </div>
                <div className="flex flex-row-reverse md:flex-row items-center gap-8">
                  {vendorData?.balance_amount > 0 && (
                    <button
                      onClick={() => {
                        if (checkPermission("/purchase/vendors", isGinne)) {
                          handlePaymentClick();
                        }
                      }}
                      className="font-semibold w-28 h-10 border rounded-lg bg-[#7EC242] text-white text-base flex justify-center items-center gap-2"
                      type="submit"
                    >
                      <p>{t("Pay Now")}</p>
                    </button>
                  )}
                  <div className="flex flex-col md:gap-2 me-16 text-gray-800 mt-5 md:mt-0">
                    <h3 className="text-sm font-medium">{t("Balance Due")}</h3>
                    <p>
                      <span className="text-xl font-semibold">
                        {"EGP"} {vendorData?.balance_amount?.toFixed(2) || "--"}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <VendorHighLowTiles data={vendorInventoryData} />
              <VendorTiles data={vendorData} />
            </div>

            {/* ── PRODUCTS SECTION ─────────────────────────────────────────── */}
            {/* Search + Export */}
            <div className="flex flex-col md:flex-row justify-between items-start me-4 mt-2 gap-4">
              <div className="flex gap-x-2 w-full md:w-[29%]">
                <div className="flex-1">
                  <SearchBox
                    placeholder={productActiveTab == 'orders' ?  t("Search Orders") : t("Search Products")}
                    searchResult={(value) => {
                      if(productActiveTab == 'orders'){
                        if (value.length >= 2 || value.length === 0) {
                        handleOrderSearchChange(value);
                      }
                      }else{
                      if (searchByShortCode || value.length >= 2 || value.length === 0) {
                        handleSearchChange(value);
                      }
                    }
                    }}
                  />
                </div>
                {productActiveTab != 'orders' && 
                   <div className="flex-1">
                  <button
                    type="button"
                    onClick={handleShortCodeToggle}
                    className={`h-[40px] px-2 rounded-md border text-sm whitespace-nowrap flex-shrink-1
                      ${searchByShortCode
                        ? "bg-blue-100 border-primaryBlue text-primaryBlue"
                        : "bg-white border-gray-300 text-gray-700"
                      }`}
                  >
                    {t("Search Code")}
                  </button>
                </div>
                }
             
              </div>
{productActiveTab != 'orders' && 
  <button
                className="w-48 h-10 bg-primaryBlue text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
                onClick={() => {
                  if (checkPermission("/purchase/vendors", isGinne)) {
                    handleExport();
                  }
                }}
              >
                <DownloadOutlined style={{ fontSize: "1.5em", MozMarginStart: -10 }} />
                <span>{t("Download in Excel")}</span>
              </button>
}
            
            </div>

            {/* Products Tabs + Table */}
            <div className="mt-6 bg-white rounded-lg">
              <div className="flex items-center justify-between px-4 pt-4 border-b">
                <div className="flex gap-6 font-semibold text-[#999999] text-base">
                  {["orders","all", "top", "bottom"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => handleProductTabChange(tab)}
                      className={`pb-2 ${productActiveTab === tab ? "text-[#272728] border-b-4 border-[#78CFEC]" : ""}`}
                    >
                      {tab == "orders" ? 
                      "Orders"
                      : tab === "all"
                        ? t("all_products")
                        : tab === "top"
                        ? t("Top 5 Products")
                        : t("Bottom 5 Products")}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-3 pt-4 mb-5">
                {tableLoading ? (
                  <ShimmerLoader />
                ) : productActiveTab === "all" ? (
                  <AllProductsTable
                    key={`${startDate}-${endDate}`}
                    tabledata={productData}
                    startDate={startDate}
                    endDate={endDate}
                    pagination={(values) => {
                      setCurrentLimit(values.limit);
                      setCurrentOffset(values.offset);
                      fetchVendorProducts(
                        searchQueryRef.current,
                        startDate,
                        endDate,
                        productActiveTab,
                        searchid,
                        values.limit,
                        values.offset,
                      );
                    }}
                  />
                ) : (productActiveTab === "top" || productActiveTab === "bottom")  ? (
                  <div className="pb-10">
                    <TopBottomProductsTable tabledata={productData} />
                  </div>
                ) : productActiveTab === "orders" ?
                <div className="-mt-3">

              <VendorPaymentTable
                    additionalData={orderData}
                    pagination={(values) => {
                      setOrderCurrentLimit(values.limit);
                      setOrderCurrentOffset(values.offset);
                      fetchOrderData(
                        orderSearchQueryRef.current,
                        orderTabToPaymentType(orderActiveTab),
                        values.offset,
                        startDateRef.current,
                        endDateRef.current,
                        searchidRef.current,
                        values.limit,
                      );
                    }}
                  />
                  </div>: <></> }
               
              </div>
            </div>

           
          </>
        </div>
      </div>

      {isModalVisible && (
        <PurchasePayment
          isOpen={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          vendor_id={vendorID}
          page={"allVendors"}
          Popup={() => setpopup(true)}
          onPaymentSuccess={refreshVendorData}
        />
      )}
    </>
  );
};

export default VendorInfo;